import {
  Avatar,
  Tooltip,
  IconButton,
  Box,
  Button,
  styled,
  InputBase,
  useTheme,
  CircularProgress,
  Icon,
  Divider,
  Card
} from '@mui/material';
import AttachFileTwoToneIcon from '@mui/icons-material/AttachFileTwoTone';
import SendTwoToneIcon from '@mui/icons-material/SendTwoTone';
import { config } from 'src/config';
import { sendPlatformSupportReply, uploadPlatformSupportAttachment, sendChatMessage, uploadChatAttachment } from 'src/queries/chat/api';
import { readChatSessionIdentity } from 'src/auth/sessionEvents';
import { useRef, useState } from 'react';
import { sendMessage, uploadFile } from 'src/helper/backend_helper';
import { CancelOutlined } from '@mui/icons-material';
import { queryClient } from 'src';
import { chatKeys } from 'src/queries/chat/keys';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
// import { socket } from './ChatContent';
import PLS from '../../../assets/images/PLS.png';

const MessageInputWrapper = styled(InputBase)(
  ({ theme }) => `
    font-size: ${theme.typography.pxToRem(18)};
    padding: ${theme.spacing(1)};
    width: 100%;
`
);

const Input = styled('input')({
  display: 'none'
});

function BottomBarContent({
  chat,
  messages,
  setMessages,

  scrollToBottom,
  socket,
  supportScope = 'legacy',
  canonicalTransport = false
}) {
  const cache = useQueryClient();
  const theme = useTheme();
  const sessionIdentity = readChatSessionIdentity();
  const requireCurrentSession = () => {
    if (readChatSessionIdentity() !== sessionIdentity) {
      throw new Error('Chat session changed; reopen the conversation.');
    }
  };
  const ref: any = useRef();

  const user = JSON.parse(localStorage.getItem('PLS'))?.user;

  const [media, setMedia] = useState(null);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('text');
  const [showMediaPopup, setShowMediaPopup] = useState(false);

  const [loading, setLoading] = useState(false);

  const handleSendMessage = async () => {
    // event.preventDefault();

    if (!message && !media) {
      return;
    }
    try {
      requireCurrentSession();
      // const receiver = chat.participants.find((e) => e.userId !== user.id);

      // #403: senderId is derived from the JWT on the backend. Sending
      // it from the client is now a 400. Identity stays implicit.
      const data = {
        chatId: chat.id,
        content: message,
        type: messageType
      };

      console.log(data);
      const headersJson = {
        headers: {
          'content-type': 'application/json'
        }
      };
      setLoading(true);
      if (messageType === 'text') {
        await (supportScope === 'platform' ? sendPlatformSupportReply(chat.id, { content: data.content, type: data.type }) : canonicalTransport ? sendChatMessage(chat.id, { content: data.content, type: data.type }) : sendMessage(data, headersJson));
        // #413: the server now emits `chat:message:new` after the REST
        // call persists the message, so the round-trip
        // `socket.emit('message', ...)` is gone. ChatContent.tsx's
        // existing `chat:message:new` subscription delivers the
        // server's canonical copy to all room members (including this
        // one) moments after the POST resolves.

        setMessage('');
      }

      setLoading(false);

      if (messageType !== 'text') {
        const headers = {
          headers: {
            'content-type': 'multipart/form-data'
          }
        };

        const formData = new FormData();
        formData.append('file', media);
        const responseFile = await (supportScope === 'platform' ? uploadPlatformSupportAttachment(chat.id, formData) : canonicalTransport ? { data: { data: await uploadChatAttachment(chat.id, media) } } : uploadFile(formData, headers));
        requireCurrentSession();
        data.content = supportScope === 'platform' || canonicalTransport ? `chat-attachments/${responseFile.data.data.storageKey}` : responseFile.data.data.filename;
        await (supportScope === 'platform' ? sendPlatformSupportReply(chat.id, { content: data.content, type: data.type, metadata: { attachmentIds: [responseFile.data.data.id] } }) : canonicalTransport ? sendChatMessage(chat.id, { content: data.content, type: data.type, metadata: { attachmentIds: [responseFile.data.data.id] } }) : sendMessage(data, headersJson));
        // #413: same removal as the text path — the server emits
        // `chat:message:new` once persistence completes.

        setMedia(null);
        setShowMediaPopup(false);
        setMessageType('text');
      }
      requireCurrentSession();
      if (canonicalTransport || supportScope === 'platform') {
        cache.invalidateQueries(chatKeys.chatMessages(chat.id));
        cache.invalidateQueries(chatKeys.myChatsRoot());
        if (supportScope === 'platform') cache.invalidateQueries(chatKeys.supportInboxRoot());
      }
      scrollToBottom();
      // event.target.reset();
      // setSendMessageLoading(false);
      updateChatMutation.mutate('');
    } catch (error) {
      setLoading(false);
      toast.error(error.message, {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'light'
      });
    }
  };

  const updateChatMutation = useMutation(
    (updatedJobData: any) => {
      return updatedJobData;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['chats']);
      },
      onError: (error) => {
        console.error('Error updating job:', error); // Optional: Handle errors
      }
    }
  );

  function handleSelectMedia(file) {
    // console.log(file);
    if (file) {
      setMedia(file);
      const type = file.type.split('/')[0];

      setMessageType(type);
      // if (type === 'audio') {
      //   setMessageType('audio');
      // }
      if (type === 'application') {
        setMessageType('file');
      }
      return setShowMediaPopup(true);
    }
    setMedia(null);
    setShowMediaPopup(false);
    setMessageType('text');
  }

  function handleMediaCancel() {
    setMedia(null);
    setShowMediaPopup(false);
    setMessageType('text');
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault(); // Prevent line break on Enter
      handleSendMessage();
    }
  };

  return (
    <Box
      sx={
        {
          // p: 2
        }
      }
    >
      {messageType === 'image' && media && (
        <Card sx={{ position: 'relative', display: 'flex', m: 2, width: 150 }}>
          <img
            src={URL.createObjectURL(media)}
            alt="message_image"
            width={150}
          />

          <Tooltip arrow placement="top" title="Cancel">
            <label htmlFor="messenger-upload-file">
              <IconButton
                color="primary"
                component="span"
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0

                  // '&:hover': {
                  //   backgroundColor: 'gray'
                  // }
                }}
                onClick={() => handleMediaCancel()}
              >
                <CancelOutlined fontSize="small" />
              </IconButton>
            </label>
          </Tooltip>
        </Card>
      )}

      {messageType !== 'image' && media && (
        <Card
          sx={{
            position: 'relative',
            display: 'flex',
            m: 1,
            p: 3,
            maxWidth: 300
          }}
        >
          <small>
            {media.name.length > 30
              ? `${media.name.slice(0, 20)}...${media.name.slice(
                  media.name.length - 10
                )}`
              : media.name}
          </small>

          <Tooltip arrow placement="top" title="Cancel">
            <label htmlFor="messenger-upload-file">
              <IconButton
                size="small"
                color="primary"
                component="span"
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0

                  // '&:hover': {
                  //   backgroundColor: 'gray'
                  // }
                }}
                onClick={() => handleMediaCancel()}
              >
                <CancelOutlined fontSize="small" />
              </IconButton>
            </label>
          </Tooltip>
        </Card>
      )}
      <Divider />

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          background: theme.colors.alpha.white[50],
          p: 2
        }}
      >
        <Box flexGrow={1} display="flex" alignItems="center">
          <Avatar
            sx={{ display: { xs: 'none', sm: 'flex' }, mr: 1 }}
            alt={user.name}
            src={PLS}
          />
          <MessageInputWrapper
            autoFocus
            placeholder="Write your message here..."
            fullWidth
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={!!media || loading}
          />
        </Box>
        <Box>
          <input
            ref={ref}
            type="file"
            onChange={(e) => handleSelectMedia(e.target.files[0])}
            hidden
            value=""
          />

          <Tooltip arrow placement="top" title="Attach a file">
            <label
              htmlFor="messenger-upload-file"
              onClick={() => ref.current.click()}
            >
              <IconButton sx={{ mx: 1 }} color="primary" component="span">
                <AttachFileTwoToneIcon fontSize="small" />
              </IconButton>
            </label>
          </Tooltip>
          {loading ? (
            <CircularProgress size={20} />
          ) : (
            <Button
              startIcon={<SendTwoToneIcon />}
              variant="contained"
              onClick={() => handleSendMessage()}
            >
              Send
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default BottomBarContent;
